/**
 * geoFeatureMapping.js
 *
 * Maps each GeoFeature id to a user-friendly Persian question and a set of
 * answer options. Each option maps to a numeric `value` that is sent to the
 * backend (PropertyRequestGeoFeature.value).
 *
 * value semantics per feature_type:
 *   score            → 0–100  (higher = more important / preferred)
 *   nearest_distance → meters (lower = closer preferred; null = don't care)
 *   custom           → feature-specific integer (see per-feature notes)
 *
 * To add a new geo feature:
 *   1. Add its id as a key below.
 *   2. Provide `question` (string) and `options` (array of { label, value }).
 *   3. Optionally set `required` default to true.
 */

const GEO_FEATURE_MAPPING = {

    // ── score features ────────────────────────────────────────────────────

    /** id 31 — acc_recreation */
    31: {
        question: 'آیا دسترسی به مراکز تفریحی و گذران اوقات فراغت برای شما مهم است؟',
        options: [
            { label: 'بله، بسیار مهم است',               value: 90 },
            { label: 'تقریباً مهم است',                   value: 60 },
            { label: 'بی‌تفاوت هستم',                    value: null },
            { label: 'مهم نیست',                          value: 20 },
        ],
    },

    /** id 32 — acc_dailyshops */
    32: {
        question: 'دسترسی آسان به فروشگاه‌ها و مراکز خرید روزانه چقدر اهمیت دارد؟',
        options: [
            { label: 'بله، بسیار مهم است — در چند دقیقه پیاده',  value: 90 },
            { label: 'تقریباً مهم است',                            value: 60 },
            { label: 'بی‌تفاوت هستم',                             value: null },
            { label: 'مهم نیست',                                   value: 20 },
        ],
    },

    /** id 5 — walkability */
    5: {
        question: 'آیا می‌خواهید محله‌ای با محیط مناسب برای پیاده‌روی داشته باشید؟',
        options: [
            { label: 'بله، پیاده‌مداری برایم بسیار مهم است',      value: 90 },
            { label: 'تا حدودی مهم است',                           value: 60 },
            { label: 'بی‌تفاوت هستم',                             value: null },
            { label: 'اهمیتی ندارد',                               value: 20 },
        ],
    },

    /** id 6 — centrality_index */
    6: {
        question: 'دسترسی به مناطق مرکزی و پررفت‌وآمد شهر چه اهمیتی برای شما دارد؟',
        options: [
            { label: 'بله، بسیار مهم است',    value: 85 },
            { label: 'تقریباً مهم است',        value: 55 },
            { label: 'بی‌تفاوت هستم',         value: null },
            { label: 'ترجیح می‌دهم آرام‌تر باشد', value: 15 },
        ],
    },

    /** id 20 — acc_secondaryschool_score */
    20: {
        question: 'آیا دسترسی به مدارس متوسطه (راهنمایی و دبیرستان) برای شما مهم است؟',
        options: [
            { label: 'بله، بسیار مهم است — فرزند دبیرستانی دارم',  value: 90 },
            { label: 'تقریباً مهم است',                              value: 60 },
            { label: 'بی‌تفاوت هستم',                               value: null },
            { label: 'اهمیتی ندارد',                                 value: 20 },
        ],
    },

    /** id 9 — acc_park_score */
    9: {
        question: 'آیا برای شما مهم است که به پارک‌های مهم با امکانات خوب دسترسی داشته باشید؟',
        options: [
            { label: 'بله، بسیار مهم است',                          value: 90 },
            { label: 'تقریباً مهم است',                              value: 60 },
            { label: 'مهم نیست',                                     value: null },
            { label: 'می‌خوام دور باشم — محیطش رو دوست ندارم',     value: 10 },
        ],
    },

    /** id 18 — acc_preschool_score */
    18: {
        question: 'آیا نزدیک بودن به مهدکودک‌های باکیفیت برای شما اهمیت دارد؟',
        options: [
            { label: 'بله، بسیار مهم است — بچه کوچک دارم',        value: 90 },
            { label: 'تقریباً مهم است',                             value: 60 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'اهمیتی ندارد',                                value: 20 },
        ],
    },

    /** id 19 — acc_primaryschool_score */
    19: {
        question: 'دسترسی به مدارس ابتدایی باکیفیت چقدر برای شما اهمیت دارد؟',
        options: [
            { label: 'بله، بسیار مهم است — بچه دبستانی دارم',     value: 90 },
            { label: 'تقریباً مهم است',                             value: 60 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'اهمیتی ندارد',                                value: 20 },
        ],
    },

    /** id 33 — acc_depth */
    33: {
        question: 'آیا می‌خواهید خانه‌تان از لحاظ دسترسی در عمق شبکه معابر باشد؟',
        options: [
            { label: 'بله، دسترسی آسان برایم مهم است',             value: 85 },
            { label: 'تا حدودی',                                    value: 55 },
            { label: 'بی‌تفاوت هستم',                              value: null },
        ],
    },

    // ── nearest_distance features ─────────────────────────────────────────
    // value = max acceptable distance in meters; null = don't care

    /** id 27 — acc_busstop */
    27: {
        question: 'دسترسی به ایستگاه اتوبوس چقدر برایتان مهم است؟',
        options: [
            { label: 'بله، بسیار مهم است — حداکثر ۳۰۰ متر',       value: 300 },
            { label: 'تقریباً مهم است — تا ۶۰۰ متر',               value: 600 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'اهمیتی ندارد',                                value: null },
        ],
    },

    /** id 28 — acc_metro */
    28: {
        question: 'نزدیکی به ایستگاه مترو چه اهمیتی برای شما دارد؟',
        options: [
            { label: 'بله، بسیار مهم است — حداکثر ۵۰۰ متر',       value: 500  },
            { label: 'تقریباً مهم است — تا ۱۰۰۰ متر',              value: 1000 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'اهمیتی ندارد',                                value: null },
        ],
    },

    /** id 29 — acc_taxi */
    29: {
        question: 'آیا نزدیک بودن به ایستگاه تاکسی برایتان مهم است؟',
        options: [
            { label: 'بله، بسیار مهم است — حداکثر ۳۰۰ متر',       value: 300 },
            { label: 'تقریباً مهم است — تا ۶۰۰ متر',               value: 600 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'اهمیتی ندارد',                                value: null },
        ],
    },

    /** id 22 — acc_cbd */
    22: {
        question: 'نزدیکی به مرکز تجاری شهر چقدر برای شما مهم است؟',
        options: [
            { label: 'بله، بسیار مهم است — حداکثر ۳ کیلومتر',     value: 3000  },
            { label: 'تقریباً مهم است — تا ۸ کیلومتر',             value: 8000  },
            { label: 'بی‌تفاوت هستم',                              value: null  },
            { label: 'ترجیح می‌دهم دورتر باشم',                    value: null  },
        ],
    },

    /** id 21 — acc_highway_dis */
    21: {
        question: 'دسترسی سریع به بزرگراه‌های اصلی چقدر اهمیت دارد؟',
        options: [
            { label: 'بله، بسیار مهم است — حداکثر ۵۰۰ متر',       value: 500  },
            { label: 'تقریباً مهم است — تا ۱۵۰۰ متر',              value: 1500 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'اهمیتی ندارد',                                value: null },
        ],
    },

    // ── custom features ───────────────────────────────────────────────────

    /** id 4 — in_trafficplan  (0 = خارج از طرح, 1 = زوج, 2 = فرد) */
    4: {
        question: 'وضعیت ملک از نظر طرح ترافیک چه اهمیتی دارد؟',
        options: [
            { label: 'خارج از طرح ترافیک — مهم است',               value: 0 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'داخل طرح باشد مشکلی ندارم',                  value: 1 },
        ],
    },

    /** id 7 — in_depricated_area  (0 = خیر, 1 = بله) */
    7: {
        question: 'آیا می‌پذیرید ملک در بافت فرسوده (مشمول بازسازی) باشد؟',
        options: [
            { label: 'خیر، حتماً خارج از بافت فرسوده باشد',        value: 0 },
            { label: 'بی‌تفاوت هستم',                              value: null },
            { label: 'بله، مشکلی ندارم',                           value: 1 },
        ],
    },

    /** id 38 — street_width  (متر: 1–20) */
    38: {
        question: 'عرض معبر (کوچه/خیابان) مجاور ملک چقدر باید باشد؟',
        options: [
            { label: 'حداقل ۱۲ متر — خیابان اصلی',                 value: 12 },
            { label: 'حداقل ۶ متر — کوچه دو طرفه',                 value: 6  },
            { label: 'اهمیتی ندارد',                                value: null },
        ],
    },
};

export default GEO_FEATURE_MAPPING;
